import React from 'react'
import styled from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { breakpoints } from '@/config/layout'

import type { FC, ReactNode } from 'react'

type MessageDetailLayoutStatic = {
  Content: typeof MessageDetailLayoutContent
  Footer: typeof MessageDetailLayoutFooter
}

export type Props = {
  /**
   * Content to be displayed
   */
  children?: ReactNode

  className?: string
}

/**
 * Maximum width for the content container
 */

const MessageDetailLayout: FC<Props> & MessageDetailLayoutStatic = ({ children, className }: Props) => {
  return (
    <StContainer className={className}>
      {/* Compound components: Include Content and Footer components */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          const { type } = child
          if (type === MessageDetailLayoutContent || type === MessageDetailLayoutFooter) {
            return child
          }
        }

        return null
      })}
    </StContainer>
  )
}

export const MessageDetailLayoutContent = ({ children }: Props) => {
  return <StContentContainer>{children}</StContentContainer>
}

export const MessageDetailLayoutFooter = ({ children }: Props) => {
  return (
    <StFooter>
      <StFooterContent>{children}</StFooterContent>
    </StFooter>
  )
}

export const StMessageDetailParagraph = styled.p`
  ${({ theme }) => theme.Typography.BodyMediumRegular};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  margin: 0;
`

// Styled components
const StContainer = styled(FlexColumn)`
  flex: 1;
  height: 100%;

  position: relative;
`

const StContentContainer = styled(FlexColumn)`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space1};
  justify-content: space-between;
  margin: 0 auto;

  @media (${breakpoints.fromM}) {
    max-width: 600px;
  }
`

const StFooterContent = styled(FlexColumn)`
  @media (${breakpoints.fromM}) {
    max-width: 600px;
  }
  margin: 0 auto;
`

const StFooter = styled.footer`
  flex-shrink: 1;

  background-color: ${({ theme }) => theme.Colors.Background.White};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space2};
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }

  position: sticky;
  align-items: center;

  bottom: 0;
  z-index: 1;
`

MessageDetailLayout.Content = MessageDetailLayoutContent
MessageDetailLayout.Footer = MessageDetailLayoutFooter

export { MessageDetailLayout }
